import { Injectable } from '@angular/core';
import { Record } from '@common/data-models/records/record';
import { IGenericAccessor } from '@common/interfaces/i-generic-accessor';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ListData, ListDataMetaData } from '@common/data-models/service/list-data';
import { ServiceCollectionScript } from '@common/data-models/service/service-collection-script';
import { ServiceLayoutColumn } from '@common/data-models/service/service-layout-column';
import { DataType } from '@common/enums/data-type';
import { CollectionRecord } from '@common/data-models/records/collection-record';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { ExportFormatRecord } from '@common/data-models/records/export-format-record';
import { UserRecord } from '@common/data-models/records/user-record';
import { KnownPropertyTypes } from '@common/enums/known-property-types';
import { Layout, LayoutColumn } from '@common/data-models/service/layout';
import { ObjectTypes } from '@common/enums/object-types';
import { RecordFactory } from '@common/data-models/records/record-factory';
import {
    ChecklistSerializer,
    SpecialSerializer,
    ComboboxSerializer, 
    LookupListSerializer,
    LocationListSerializer,
    LocationListComplexSerializer,
    AddressSerializer,
    AttachmentListSerializer,
    StaffIDListSerializer,
    ViewStateFilterSerializer,
    ViewStateOptionsSerializer,
    ViewStateDelaySerializer,
    ViewStateSequenceSerializer,
    RichTextSerializer,
    PreviewSerializer,
    YoutubeSerializer,
    CustomFieldsSerializer,
    RelationshipSerializer,
    ViewStateOrderSerializer,
    DateTimeRangeSerializer
} from '@common/data-models/serializers/serializers';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';

import { IGlobalTablesService } from '@common/interfaces/i-global-tables-service';
import { GlobalTablesService } from "@common/services/global-tables-service";
import { Path } from "@common/data-models/complex-types/path";
import { ObjectProperties } from "@common/data-models/service/object-properties";
import { DateTime } from "@suite/common/data-models/complex-types/date-time";
import { DataStore } from '@common/data-models/data-store/data-store';

@Injectable({ providedIn: 'root' })
export class Convertor {
    public globalTablesService: IGlobalTablesService;

    constructor() { }

    public serialize(rec: Record, sanitize = false): { [id: string]: any } {

        let data: { [id: string]: any } = {}
        let viewStateType = 0;
        if (rec.type == ObjectTypes.PSTVIEWSTATE) {
            viewStateType = rec.getAs<number>(PSTFieldNames.PSTViewStateType);
        }

        let type = rec.type;

        for (let key of rec.keys) {

            // Remove fields that are not in field defs
            if (sanitize && !this.globalTablesService.FieldDefsDict[key]) {
                continue;
            }

            let value = rec.data[key];
            if (key == PSTFieldNames.PSTExternalFiles) {
                data[key] = AttachmentListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == "Relationships") {
                data[key] = RelationshipSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == "text") {
                data["Text"] = value;
                data["text"] = value;
            } else if (this.globalTablesService.isCheckListFieldType(key)) {
                data[key] = ChecklistSerializer.Instance.serialize(value);
            } else if (key.toLowerCase() == "guid") {
                data[key] = value;
            } else if (key == "Path") {
                data[key] = (<Path>value).fullPath;
            } else if (this.globalTablesService.isComboboxFieldType(key)) {
                data[key] = ComboboxSerializer.Instance.serialize(value);
            } else if (this.globalTablesService.isLookupListFieldType(key)) {
                data[key] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isCustomItemFieldType(key)) {
                data[key] = CustomFieldsSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isSpecialFieldType(key)) {
                data[key] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isAddressFieldType(key)) {
                data[key] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.FieldDefsDict[key] && this.globalTablesService.isStaffIDListFieldType(key)) {
                data[key] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, key);
            } else if (key == PSTFieldNames.RSLocation) {
                data[key] = LocationListComplexSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isLocationListFieldType(key)) {
                data[key] = LocationListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == PSTFieldNames.PSTViewStateFilter) {
                data[key] = ViewStateFilterSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == PSTFieldNames.PSTTopicOrder) {
                data[key] = ViewStateOrderSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isDateRangeFieldType(key)) {
                data[key] = DateTimeRangeSerializer.Instance().serialize(value);
            } else if (value instanceof DateTime) {
                data[key] = value.moment?.toISOString() || value.get()?.toISOString();
            } else if (
                key == PSTFieldNames.PSTViewStateGrouping ||
                key == PSTFieldNames.PSTViewStateSorting ||
                key == PSTFieldNames.PSTViewStateLayout) {
                data[key] = ViewStateOptionsSerializer.Instance(this.globalTablesService).serialize(value, key, viewStateType);
            } else if (key == PSTFieldNames.PSTViewStateSlideshowSequence) {
                data[key] = ViewStateSequenceSerializer.Instance.serialize(value);
            } else if (key == PSTFieldNames.PSTViewStateSlideshowDelay) {
                data[key] = ViewStateDelaySerializer.Instance.serialize(value);
            } else {
                data[key] = value;
            }
        }

        if (sanitize) data["Type"] = type;

        return data;
    }

    public deserialize(rec: Record, data: IGenericAccessor, cachedMetaData?: { [id: string]: number }) {
        let objectType = data.getRawValue("Type");
        let viewStateType = 0;
        if ((<number>objectType) == ObjectTypes.PSTVIEWSTATE) {
            viewStateType = data.getRawValue(PSTFieldNames.PSTViewStateType);
        }
        let metaData = cachedMetaData ? cachedMetaData : data.getMetaData();

        for (let key in metaData) {
            let fullPath = DataType.fromRaw(data.getRawValue("Path"), DataType.STRING);
            if (fullPath && (fullPath.toString().indexOf("\\L\\") > -1) || fullPath && (fullPath.toString().indexOf("\\L") > -1)) { // template
                if (
                    (key != "parentCollectionGuid") &&
                    (key != "Database") &&
                    ((!this.globalTablesService.FieldDefsDict[key]) ||
                        !Record.allPropertiesCache[objectType][key]) &&
                    (!ListDataMetaData[key])
                ) {
                    continue;
                }
            }

            if (key == PSTFieldNames.PSTExternalFiles) {
                rec.set(key, AttachmentListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key == "Relationships") {
                rec.set(key, RelationshipSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isCheckListFieldType(key)) {
                rec.set(key, ChecklistSerializer.Instance.deserialize(data, key, objectType));
            } else if (key.toLowerCase() == "guid") {
                rec.guid = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key == "Path") {
                let fullPath = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
                rec.fullPath = fullPath;
            } else if (this.globalTablesService.isComboboxFieldType(key)) {
                rec.set(key, ComboboxSerializer.Instance.deserialize(data, key));
            } else if (this.globalTablesService.isLookupListFieldType(key)) {
                rec.set(key, LookupListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isCustomItemFieldType(key)) {
                rec.set(key, CustomFieldsSerializer.Instance(this.globalTablesService).deserialize(data, key))
            } else if (this.globalTablesService.isSpecialFieldType(key)) {
                rec.set(key, SpecialSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isAddressFieldType(key)) {
                rec.set(key, AddressSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isStaffIDListFieldType(key)) {
                rec.set(key, StaffIDListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key == PSTFieldNames.RSLocation) {
                rec.set(key, LocationListComplexSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isLocationListFieldType(key)) {
                rec.set(key, LocationListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key == PSTFieldNames.PSTViewStateFilter) {
                rec.set(key, ViewStateFilterSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType));
            } else if (key == "text" || key == "Text") {
                rec.set("text", RichTextSerializer.Instance.deserialize(data, key, objectType));
            } else if (
                key == PSTFieldNames.PSTViewStateGrouping ||
                key == PSTFieldNames.PSTViewStateSorting ||
                key == PSTFieldNames.PSTViewStateLayout) {
                rec.set(key, ViewStateOptionsSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType));
            } else if (key == PSTFieldNames.PSTViewStateSlideshowDelay) {
                rec.set(key, ViewStateDelaySerializer.Instance.deserialize(data, key));
            } else if (key == PSTFieldNames.PSTTopicOrder) {
                rec.set(key, ViewStateOrderSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isDateRangeFieldType(key)) {
                rec.set(key, DateTimeRangeSerializer.Instance().deserialize(data, key));
            } else if (key == "TextCommands") {
                if (objectType == ObjectTypes.WIRE) {
                    let preview = PreviewSerializer.Instance.deserialize(data, key, objectType);
                    rec.set(PSTFieldNames.PSTWireImage, preview);
                    let youtube = YoutubeSerializer.Instance.deserialize(data, key, objectType);
                    rec.set(PSTFieldNames.PSTWireVideo, youtube);
                }
            } else if (key == PSTFieldNames.PSTWirePublished) {
				rec.set(key, DataType.fromRaw(data.getRawValue(key), DataType.PUBLISHEDDATE));
			} else {
                // console.log(key + " = " + data.getRawValue(key));
                // console.log(" -> " + key + " = " + DataType.fromRaw(data.getRawValue(key), metaData[key]));
                rec.set(key, DataType.fromRaw(data.getRawValue(key), metaData[key]));
            }
        }
    }

    public fromServiceRecordImpl<T extends Record>(
        data: IGenericAccessor,
        cachedMetaData?: { [id: string]: number },
        createMethod?: (number) => T
    ): T {
        let ret = <T>createMethod(data.getRawValue("Type"));
        if (!ret) return;
        this.deserialize(ret, data, cachedMetaData);
        // ret.generateGetters(); WE dont need this util "Live" possibly
        return ret;
    }

    public fromServiceRecord<T extends Record>(data: IGenericAccessor, cachedMetaData?: { [id: string]: number }): T {
        return this.fromServiceRecordImpl<T>(data, cachedMetaData, RecordFactory.createRecordForType);
    }

    public fromServiceRecordTemplate<T extends Record>(data: IGenericAccessor, cachedMetaData?: { [id: string]: number }): T {
        return this.fromServiceRecordImpl<T>(data, cachedMetaData, RecordFactory.createTemplateForType);
    }

    public fromServiceLayout(defaultLayout: {
        DefaultLayout: Array<ServiceLayoutColumn>,
        DefaultLayoutType: number
    }): Layout {
        let ret = new Layout();
        ret.type = defaultLayout.DefaultLayoutType;
        for (let serviceCol of defaultLayout.DefaultLayout) {
            let col = new LayoutColumn();
            col.autoSizeWidth = serviceCol.ColumnAutoSizeWidth;
            col.displayName = serviceCol.ColumnDisplayName;
            col.extractTarget = serviceCol.ColumnExtractTarget;
            col.justification = serviceCol.ColumnJustification;
            col.list = serviceCol.ColumnList;
            col.maxLen = serviceCol.ColumnMaxLen;
            col.name = serviceCol.ColumnName;
            col.validation = serviceCol.ColumnValidation;
            col.width = serviceCol.ColumnWidth;
            col.wordWrap = serviceCol.ColumnWordWrap
            col.isReadOnly = serviceCol.IsReadOnly;
            col.sortOrder = serviceCol.SortOrder;
            ret.columns.push(col);
        }
        // Sort
        ret.columns = ret.columns.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
        });
        return ret;
    }

    /*
      private fromServiceRecordImpl<T extends Record>(data: ServiceRecord, constr: new () => T): T {
        let ret = new constr();
        data.ListData = Object.assign(new ListData(), data.ListData);
        this.populateRecord(ret, data, {});
        return ret;
      }
    */

    private fromServiceCollectionRecordImpl<T extends CollectionRecord>(data: ServiceCollectionRecord, createMethod: (number) => T): T {
        let timeStart = new Date().getTime();
        let ret = <T>createMethod(data.getRawValue("Type"));
        let layoutTypeMaps = {};
        if (data.DefaultLayout) {
            for (let col of data.DefaultLayout.DefaultLayout) {
                layoutTypeMaps[col.ColumnName] = KnownPropertyTypes.forValidationType(col.ColumnValidation);
            }
        }
        KnownPropertyTypes.setFor(layoutTypeMaps);
        ret.guidsToBeRemoved = data.RemovedScripts ? data.RemovedScripts : [];
        this.deserialize(ret, data);
        let cachedMetaData: { [id: string]: number } = null;
        let children: Array<Record> = [];
        if (data.CollectionScripts) {
            if (data.CollectionScripts) {
                for (let i = 0; i < data.CollectionScripts.length; i++) {
                    let serviceCollectionScriptData = data.CollectionScripts[i];
                    let serviceCollectionScript = Object.assign(new ServiceCollectionScript, serviceCollectionScriptData);
                    serviceCollectionScript.instantiateChildren();
                    cachedMetaData = serviceCollectionScript.getMetaData();
                    let child = this.fromServiceRecord(serviceCollectionScript, cachedMetaData);
                    children.push(child);
                }
                ret.setChildren(children);
            }
        }
        return ret;
    }

    public fromServiceCollectionRecord<T extends CollectionRecord>(data: ServiceCollectionRecord): T {
        let ret: T = null;
        if (data.IsCollectionDiff) {
            if (!data.ListData.Type) {
                let existingRecord = DataStore.byGuid<Record>(data.ListData.Guid, "");
                if (existingRecord) {
                    data.ListData.Type = existingRecord.type;
                } else {
                    return null;
                }
            }
            for (let collectionScript of data.CollectionScripts) {
                if (!collectionScript["RecordPointer"].ListData.Type) {
                    let existingRecord = DataStore.byGuid<Record>(collectionScript["RecordPointer"].ListData.Guid, "");
                    if (existingRecord) {
                        collectionScript["RecordPointer"].ListData.Type = existingRecord.type;
                    } else {
                        return null;
                    }
                }
            }
        }
        ret = this.fromServiceCollectionRecordImpl<T>(data, RecordFactory.createRecordForType);
        if (data.DefaultLayout) ret.layout = this.fromServiceLayout(data.DefaultLayout);
        return ret;
    }

    public fromServiceCollectionTemplateRecord<T extends CollectionRecord>(data: ServiceCollectionRecord): T {
        return this.fromServiceCollectionRecordImpl<T>(data, RecordFactory.createTemplateForType);
    }

    public fromServiceViewStateRecord<T extends ViewStateRecord>(data: any): T {
        data.instantiateChildren();
        let viewStateType = data.getRawValue("PSTViewStateType");
        let rec = RecordFactory.createViewStateForType<T>(viewStateType);
        this.deserialize(rec, data);
        return rec;
    }

    public toServiceViewStateRecord(rec: ViewStateRecord): ServiceRecord {
        let serviceRecord: ServiceRecord = new ServiceRecord();
        serviceRecord.ListData = new ListData();

        let data = this.serialize(rec);
        let properties: { [id: string]: any } = {};

        for (let key in data) {
            if (ListDataMetaData[key]) {
                serviceRecord.ListData[key] = data[key];
            } else {
                properties[key] = data[key];
            }
        }
        serviceRecord.ObjectProperties = this.toENPSFields(properties);
        serviceRecord.ObjectPropertiesCollection = new ObjectProperties(serviceRecord.ObjectProperties);

        return serviceRecord;
    }

    public fromServiceExportFormatRecord(data: ServiceRecord): ExportFormatRecord {
        data.instantiateChildren();
        return <ExportFormatRecord>this.fromServiceRecord(data);
    }

    public fromServiceUserRecord(data: ServiceCollectionRecord): UserRecord {
        data.instantiateChildren();
        return <UserRecord>this.fromServiceRecord(data);
    }

    public toENPSFields(obj: { [id: string]: any }): Array<{ FieldName: string, FieldValue: any }> {
        let ret = new Array<{ FieldName: string, FieldValue: any }>();
        for (let prop in obj) {
            if (prop == 'ExternalFiles') {
                if (obj[prop].items.length == 0) {
                    ret.push({ FieldName: prop, FieldValue: "" });
                } else {
                    ret.push({ FieldName: prop, FieldValue: AttachmentListSerializer.Instance(this.globalTablesService).serialize(obj[prop]) });
                }
            } else {
                ret.push({ FieldName: prop, FieldValue: (typeof obj[prop] === "undefined") ? "" : obj[prop] });
            }
        }
        return ret;
    }
}
